import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, MagnifyingGlass, Microphone, Paperclip, PaperPlaneTilt, Smiley, UserCircle, X } from '@phosphor-icons/react';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { apiErrorMessage, cn, fmtTime } from '@/lib/utils';
import { Badge, Button, EmptyState, Input, Label, Spinner, Textarea, useToast } from '@/components/ui-kit';
import { useInstances } from '@/context/InstanceContext';

function initials(name, phone) {
  const source = (name && name !== phone ? name : phone) || '?';
  return source
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function TagsEditor({ value, onChange }) {
  const [tag, setTag] = useState('');
  const tags = value || [];
  const addTag = () => {
    const clean = tag.trim();
    if (!clean || tags.includes(clean)) return;
    onChange([...tags, clean]);
    setTag('');
  };
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {tags.map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onChange(tags.filter((current) => current !== item))}
            className="rounded-full border px-3 py-1 text-xs"
            style={{ borderColor: 'var(--s1-card-border)', color: 'var(--s1-accent-text)', background: 'var(--s1-accent-soft)' }}
          >
            {item} ×
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="nova tag" onKeyDown={(e) => e.key === 'Enter' && addTag()} />
        <Button type="button" variant="secondary" onClick={addTag}>Adicionar</Button>
      </div>
    </div>
  );
}

const QUICK_EMOJIS = ['😀', '😊', '🙏', '👍', '❤️', '🔥', '👏', '🙌', '😉', '🤝', '✅', '✨'];

function mediaTypeFromFile(file) {
  const type = file?.type || '';
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return 'document';
}

function MessageMedia({ message, onPreview }) {
  if (!message.mediaUrl) return null;
  if (message.mediaType === 'image') {
    return (
      <button type="button" onClick={() => onPreview(message)} className="mb-2 block cursor-zoom-in">
        <img
          src={message.mediaUrl}
          alt={message.fileName || 'Imagem recebida'}
          className="max-h-[300px] max-w-[min(100%,360px)] rounded-lg object-contain"
          loading="lazy"
        />
      </button>
    );
  }
  if (message.mediaType === 'video') {
    return <video src={message.mediaUrl} controls preload="metadata" className="mb-2 max-h-[420px] max-w-full rounded-lg" />;
  }
  if (message.mediaType === 'audio') {
    return <audio src={message.mediaUrl} controls preload="metadata" className="mb-2 max-w-full" />;
  }
  return (
    <a href={message.mediaUrl} target="_blank" rel="noreferrer" className="mb-2 block underline">
      {message.fileName || 'Abrir documento'}
    </a>
  );
}

export default function InboxPage() {
  const { instances, current } = useInstances();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedInstance = searchParams.get('instanceName') || searchParams.get('instance') || '';
  const [instanceName, setInstanceName] = useState(requestedInstance);
  const [query, setQuery] = useState('');
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [thread, setThread] = useState({ conversation: null, contact: null, messages: [] });
  const [reply, setReply] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [mobileThreadOpen, setMobileThreadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [stages, setStages] = useState([]);
  const toast = useToast();

  const defaultInstance = current || instances[0]?.instanceName || '';
  const activeInstance = instanceName === '__all__' ? '' : (instanceName || defaultInstance);
  const instanceSelectValue = instanceName === '__all__' ? '__all__' : activeInstance;
  const activeContact = thread.contact;
  const requestedConversation = searchParams.get('conversation') || '';
  const requestedConversationRef = useRef(requestedConversation);
  const toastRef = useRef(toast);
  const attachmentInputRef = useRef(null);
  const audioInputRef = useRef(null);

  useEffect(() => {
    requestedConversationRef.current = requestedConversation;
    if (requestedConversation) setMobileThreadOpen(true);
  }, [requestedConversation]);

  useEffect(() => {
    if (requestedInstance) setInstanceName(requestedInstance);
  }, [requestedInstance]);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  useEffect(() => {
    if (!previewMedia) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setPreviewMedia(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [previewMedia]);

  const loadConversations = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await api.crmConversations({ instanceName: activeInstance || undefined, q: query || undefined });
      setConversations(rows);
      setActiveId((currentId) => {
        const requestedId = requestedConversationRef.current;
        if (requestedId && rows.some((row) => row.id === requestedId)) return requestedId;
        if (currentId && rows.some((row) => row.id === currentId)) return currentId;
        return rows[0]?.id || '';
      });
    } catch (e) {
      toastRef.current.show(apiErrorMessage(e), 'error');
    } finally {
      setLoading(false);
    }
  }, [activeInstance, query]);

  useEffect(() => {
    api.crmStages().then(setStages).catch(() => setStages([]));
  }, []);

  useEffect(() => {
    loadConversations();
    const timer = setInterval(loadConversations, 9000);
    return () => clearInterval(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (!activeId) {
      setThread({ conversation: null, contact: null, messages: [] });
      return;
    }
    api.crmMessages(activeId)
      .then(setThread)
      .catch((e) => toast.show(apiErrorMessage(e), 'error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const saveContact = async (patch) => {
    if (!activeContact?.id) return;
    setSavingContact(true);
    try {
      const updated = await api.updateCrmContact(activeContact.id, patch);
      setThread((prev) => ({ ...prev, contact: updated }));
      await loadConversations();
      toast.show('Contato atualizado', 'success');
    } catch (e) {
      toast.show(apiErrorMessage(e), 'error');
    } finally {
      setSavingContact(false);
    }
  };

  const sendReply = async () => {
    const text = reply.trim();
    if (!activeId || (!text && !replyFile)) return;
    setSending(true);
    try {
      let mediaPayload = {};
      if (replyFile) {
        const uploaded = await api.uploadMedia(replyFile);
        mediaPayload = {
          mediaUrl: uploaded.url,
          mediaType: mediaTypeFromFile(replyFile),
          fileName: replyFile.name,
          mimetype: replyFile.type,
        };
      }
      await api.crmSendReply(activeId, { text, ...mediaPayload });
      setReply('');
      setReplyFile(null);
      setEmojiOpen(false);
      const fresh = await api.crmMessages(activeId);
      setThread(fresh);
      await loadConversations();
    } catch (e) {
      toast.show(apiErrorMessage(e), 'error');
    } finally {
      setSending(false);
    }
  };

  const selectedConversation = useMemo(
    () => conversations.find((item) => item.id === activeId),
    [conversations, activeId]
  );

  return (
    <div className="space-y-8">
      {toast.node}
      {previewMedia && (
        <div
          className="fixed inset-0 z-[100] grid place-items-center bg-black/80 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Visualização da imagem"
          onClick={() => setPreviewMedia(null)}
        >
          <div className="relative flex max-h-[92vh] max-w-[92vw] flex-col items-center gap-3" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              className="absolute right-2 top-2 z-10 grid h-10 w-10 place-items-center rounded-full bg-black/70 text-white transition hover:bg-black"
              aria-label="Fechar visualização"
            >
              <X size={22} />
            </button>
            <img
              src={previewMedia.mediaUrl}
              alt={previewMedia.fileName || 'Imagem recebida'}
              className="max-h-[86vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
            />
            <a
              href={previewMedia.mediaUrl}
              target="_blank"
              rel="noreferrer"
              className="rounded-full border border-white/30 bg-black/60 px-4 py-2 text-xs text-white hover:bg-black"
            >
              Abrir original
            </a>
          </div>
        </div>
      )}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">// ATENDIMENTO 1X1</div>
          <h1 className="page-title">Inbox</h1>
          <p className="mt-2 max-w-2xl" style={{ color: 'var(--s1-text-2)' }}>
            Responda conversas privadas da Evolution sem sair do Groups.
          </p>
        </div>
        <select
          value={instanceSelectValue}
          onChange={(e) => setInstanceName(e.target.value)}
          className="w-full rounded-[var(--s1-radius-control)] border px-3 py-2 text-sm sm:w-auto sm:min-w-[220px]"
          style={{ background: 'var(--s1-input)', borderColor: 'var(--s1-card-border)', color: 'var(--s1-text)' }}
        >
          <option value="__all__">Todas as instâncias</option>
          {instances.map((item) => (
            <option key={item.instanceName} value={item.instanceName}>{item.instanceName}</option>
          ))}
        </select>
      </header>

      <section
        className="grid min-h-[calc(100dvh-160px)] grid-cols-1 overflow-hidden rounded-[var(--s1-radius-panel)] border lg:h-[calc(100vh-210px)] lg:min-h-[650px] lg:grid-cols-[320px_minmax(420px,1fr)_320px]"
        style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-panel)' }}
      >
        <aside
          className={cn(
            'min-h-[calc(100dvh-160px)] min-w-0 flex-col border-b lg:flex lg:min-h-0 lg:max-h-none lg:border-b-0 lg:border-r',
            mobileThreadOpen ? 'hidden' : 'flex'
          )}
          style={{ borderColor: 'var(--s1-line)' }}
        >
          <div className="border-b p-5" style={{ borderColor: 'var(--s1-line)' }}>
            <div className="flex items-center justify-between">
              <h2 className="section-title">Conversas</h2>
              <Badge variant="info">{conversations.length} ativas</Badge>
            </div>
            <div className="relative mt-4">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--s1-muted)' }} />
              <Input className="pl-9" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="buscar contato ou mensagem" />
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-3">
            {loading && !conversations.length ? (
              <div className="grid h-full place-items-center"><Spinner /></div>
            ) : conversations.length ? conversations.map((conversation) => (
              <button
                key={conversation.id}
                type="button"
                onClick={() => {
                  setActiveId(conversation.id);
                  setSearchParams({
                    conversation: conversation.id,
                    instanceName: conversation.instanceName || activeInstance,
                  });
                  setMobileThreadOpen(true);
                }}
                className={cn('mb-2 flex w-full gap-3 rounded-[var(--s1-radius-card)] border p-3 text-left transition', activeId === conversation.id && 'shadow-lg')}
                style={{
                  background: activeId === conversation.id ? 'var(--s1-accent-soft)' : 'transparent',
                  borderColor: activeId === conversation.id ? 'var(--s1-accent)' : 'transparent',
                }}
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border font-semibold" style={{ borderColor: 'var(--s1-gold)', color: 'var(--s1-gold-text)' }}>
                  {initials(conversation.contactName, conversation.phone)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center justify-between gap-2">
                    <strong className="truncate text-sm" style={{ color: 'var(--s1-text)' }}>{conversation.contactName || conversation.phone}</strong>
                    <span className="text-xs" style={{ color: 'var(--s1-muted)' }}>{fmtTime(conversation.lastAt)}</span>
                  </span>
                  <span className="mt-1 block truncate text-xs" style={{ color: 'var(--s1-text-2)' }}>{conversation.lastMessage}</span>
                  {conversation.unread > 0 && <Badge className="mt-2" variant="warn">{conversation.unread} novas</Badge>}
                </span>
              </button>
            )) : (
              <EmptyState icon={UserCircle} title="Sem conversas ainda" hint="As mensagens privadas recebidas pela Evolution aparecerão aqui." />
            )}
          </div>
        </aside>

        <main
          className={cn(
            'min-h-[calc(100dvh-160px)] min-w-0 flex-col lg:flex lg:min-h-0',
            mobileThreadOpen ? 'flex' : 'hidden'
          )}
        >
          <div className="flex items-center gap-3 border-b p-5" style={{ borderColor: 'var(--s1-line)' }}>
            <button
              type="button"
              onClick={() => setMobileThreadOpen(false)}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border lg:hidden"
              style={{ borderColor: 'var(--s1-card-border)', color: 'var(--s1-text)', background: 'var(--s1-card)' }}
              aria-label="Voltar para conversas"
            >
              <ArrowLeft size={20} />
            </button>
            <span className="grid h-12 w-12 place-items-center rounded-full border font-semibold" style={{ borderColor: 'var(--s1-gold)', color: 'var(--s1-gold-text)' }}>
              {initials(selectedConversation?.contactName, selectedConversation?.phone)}
            </span>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold" style={{ color: 'var(--s1-text)' }}>
                {selectedConversation?.contactName || 'Selecione uma conversa'}
              </h2>
              {selectedConversation && <p className="text-xs" style={{ color: 'var(--s1-muted)' }}>{selectedConversation.phone} · {selectedConversation.instanceName}</p>}
            </div>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 sm:p-6">
            {thread.messages.map((message) => (
              <div key={message.id} className={cn('flex', message.direction === 'outbound' ? 'justify-end' : 'justify-start')}>
                <div
                  className="max-w-[88%] rounded-[var(--s1-radius-card)] border px-4 py-3 text-sm sm:max-w-[72%]"
                  style={{
                    background: message.direction === 'outbound' ? 'var(--s1-accent-soft)' : 'var(--s1-card)',
                    borderColor: message.direction === 'outbound' ? 'var(--s1-accent)' : 'var(--s1-card-border)',
                    color: 'var(--s1-text)',
                  }}
                >
                  <MessageMedia message={message} onPreview={setPreviewMedia} />
                  {(!message.mediaUrl || !/^\[(imagem|vídeo|áudio|documento|figurinha)\]$/i.test(message.text || '')) && (
                    <p className="whitespace-pre-wrap">{message.text}</p>
                  )}
                  <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px]" style={{ color: 'var(--s1-muted)' }}>
                    <span>{fmtTime(message.createdAt)}</span>
                    {message.direction === 'outbound' && (message.deliveryStatus === 'pending' || (!message.deliveryStatus && message.rawEvent === 'broadcast')) && (
                      <span
                        className="rounded-full border px-2 py-0.5 uppercase tracking-[0.18em]"
                        style={{ borderColor: 'var(--s1-gold)', color: 'var(--s1-gold-text)', background: 'var(--s1-accent-soft)' }}
                      >
                        {message.deliveryStatus === 'pending' ? 'pendente' : 'sem confirmacao'}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t p-4" style={{ borderColor: 'var(--s1-line)' }}>
            <input
              ref={attachmentInputRef}
              type="file"
              className="hidden"
              accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
              onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
            />
            <input
              ref={audioInputRef}
              type="file"
              className="hidden"
              accept="audio/*,.ogg"
              onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
            />
            {replyFile && (
              <div
                className="mb-2 flex items-center justify-between gap-3 rounded-[var(--s1-radius-control)] border px-3 py-2 text-xs"
                style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-accent-soft)', color: 'var(--s1-text)' }}
              >
                <span className="min-w-0 truncate">{mediaTypeFromFile(replyFile)} · {replyFile.name}</span>
                <button type="button" onClick={() => setReplyFile(null)} className="shrink-0" aria-label="Remover anexo">
                  <X size={16} />
                </button>
              </div>
            )}
            <div className="relative flex items-end gap-2 rounded-[var(--s1-radius-card)] border p-2 sm:gap-3 sm:p-3" style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-card)' }}>
              {emojiOpen && (
                <div
                  className="absolute bottom-full left-3 mb-2 grid grid-cols-6 gap-1 rounded-[var(--s1-radius-card)] border p-2 shadow-xl"
                  style={{ borderColor: 'var(--s1-card-border)', background: 'var(--s1-panel)' }}
                >
                  {QUICK_EMOJIS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      className="grid h-8 w-8 place-items-center rounded-md text-lg hover:bg-white/10"
                      onClick={() => setReply((current) => `${current}${emoji}`)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition hover:bg-white/10"
                style={{ color: 'var(--s1-muted)' }}
                onClick={() => attachmentInputRef.current?.click()}
                aria-label="Enviar anexo"
              >
                <Paperclip size={20} />
              </button>
              <button
                type="button"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition hover:bg-white/10"
                style={{ color: 'var(--s1-muted)' }}
                onClick={() => audioInputRef.current?.click()}
                aria-label="Enviar áudio"
              >
                <Microphone size={20} />
              </button>
              <button
                type="button"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition hover:bg-white/10"
                style={{ color: emojiOpen ? 'var(--s1-accent-text)' : 'var(--s1-muted)' }}
                onClick={() => setEmojiOpen((open) => !open)}
                aria-label="Abrir emojis"
              >
                <Smiley size={20} />
              </button>
              <Textarea
                className="min-h-[44px] resize-none border-0 bg-transparent font-sans focus:ring-0"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Escreva uma resposta..."
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') sendReply();
                }}
              />
              <Button type="button" size="icon" className="shrink-0 rounded-full" onClick={sendReply} disabled={(!reply.trim() && !replyFile) || sending || !activeId}>
                {sending ? <Spinner /> : <PaperPlaneTilt size={17} weight="fill" />}
              </Button>
            </div>
          </div>
        </main>

        <aside className="hidden max-h-[720px] min-h-0 flex-col border-t lg:flex lg:max-h-none lg:border-l lg:border-t-0" style={{ borderColor: 'var(--s1-line)' }}>
          <div className="border-b p-6 text-center" style={{ borderColor: 'var(--s1-line)' }}>
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full border text-xl font-semibold" style={{ borderColor: 'var(--s1-gold)', color: 'var(--s1-gold-text)' }}>
              {initials(activeContact?.name, activeContact?.phone)}
            </div>
            <h2 className="mt-3 text-xl font-semibold" style={{ color: 'var(--s1-text)' }}>{activeContact?.name || 'Contato'}</h2>
            <p className="text-sm" style={{ color: 'var(--s1-muted)' }}>{activeContact?.phone}</p>
          </div>
          {activeContact ? (
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">
              <div>
                <Label>Nome</Label>
                <Input value={activeContact.name || ''} onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, name: e.target.value } }))} />
              </div>
              <div>
                <Label>Etapa</Label>
                <select
                  value={activeContact.stage || 'novo'}
                  onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, stage: e.target.value } }))}
                  className="w-full rounded-[var(--s1-radius-control)] border px-3 py-2 text-sm"
                  style={{ background: 'var(--s1-input)', borderColor: 'var(--s1-card-border)', color: 'var(--s1-text)' }}
                >
                  {stages.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Valor de pedido</Label>
                <Input
                  value={activeContact.orderValue || ''}
                  onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, orderValue: e.target.value } }))}
                  placeholder="Ex: R$ 10.000,00"
                />
              </div>
              <div>
                <Label>Produtos</Label>
                <Textarea
                  rows={3}
                  value={activeContact.products || ''}
                  onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, products: e.target.value } }))}
                  placeholder="Ex: Plano completo, kit home care..."
                />
              </div>
              <div>
                <Label>Entrada de pagamento</Label>
                <Input
                  value={activeContact.paymentEntry || ''}
                  onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, paymentEntry: e.target.value } }))}
                  placeholder="Ex: 20% na reserva"
                />
              </div>
              <div>
                <Label>Método de pagamento</Label>
                <Input
                  value={activeContact.paymentMethod || ''}
                  onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, paymentMethod: e.target.value } }))}
                  placeholder="Ex: Pix, cartão, boleto"
                />
              </div>
              <div>
                <Label>Tags</Label>
                <TagsEditor value={activeContact.tags || []} onChange={(tags) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, tags } }))} />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea rows={5} value={activeContact.notes || ''} onChange={(e) => setThread((prev) => ({ ...prev, contact: { ...prev.contact, notes: e.target.value } }))} />
              </div>
              <Button
                type="button"
                className="w-full"
                disabled={savingContact}
                onClick={() => saveContact({
                  name: activeContact.name,
                  stage: activeContact.stage,
                  tags: activeContact.tags || [],
                  notes: activeContact.notes || '',
                  orderValue: activeContact.orderValue || '',
                  products: activeContact.products || '',
                  paymentEntry: activeContact.paymentEntry || '',
                  paymentMethod: activeContact.paymentMethod || '',
                })}
              >
                {savingContact ? <Spinner /> : 'Salvar contato'}
              </Button>
            </div>
          ) : (
            <div className="p-5"><EmptyState title="Nada selecionado" hint="Escolha uma conversa para ver os dados do lead." /></div>
          )}
        </aside>
      </section>
    </div>
  );
}
